import { useEffect, useRef, useState } from 'react'
import Cookies from 'js-cookie'
import {
  initAmplitude,
  initFacebookPixel,
  initBI,
  initGoogleTag,
  sendAmplitudeData,
  sendFacebookPixelTrack,
} from '@analytics'
import {
  COOKIE_UUID_KEY,
  EVENTS,
  FB_PIXEL_ID,
  GTM_ID,
  HOTJAR_ID,
  IS_DEV,
  IS_PROD,
} from '@constants'
import { tiktok, hotjar } from '@scripts'
import { queryParser, useInlineScript } from '@applyft-web/utils'
import { isBingTraffic, safeStorageSet } from '@utils'

const GTM_LOAD_TIMEOUT_MS = 10_000
const GTM_POLL_MS = 250

const isGtmLoaded = () => Boolean(window.google_tag_manager)

export const useInitAnalytics = () => {
  const { fbclid, ttclid } = queryParser(window.location.search)
  const tiktokScript = ttclid ? tiktok : ''
  const hotjarScript = HOTJAR_ID ? hotjar : ''
  const initCallback = (analytics: string) => {
    safeStorageSet(`${analytics}Init`, 'true')
  }
  const inited = useRef(false)
  const amplitudeInited = useRef(false)
  const [gtmLoaded, setGtmLoaded] = useState(isGtmLoaded)
  // GTM sets CookieYes consent defaults only for Bing traffic, so only that traffic waits
  // for the container before Google Analytics and Google Ads start.
  const isWaitingForGtm = isBingTraffic() && !gtmLoaded

  useEffect(() => {
    if (!isWaitingForGtm) return
    if (isGtmLoaded()) {
      setGtmLoaded(true)
      return
    }

    const interval = setInterval(() => {
      if (!isGtmLoaded()) return
      setGtmLoaded(true)
      clearInterval(interval)
    }, GTM_POLL_MS)

    return () => clearInterval(interval)
  }, [isWaitingForGtm])

  useEffect(() => {
    if (inited.current || isWaitingForGtm) return
    initGoogleTag()
    inited.current = true
  }, [isWaitingForGtm])

  useEffect(() => {
    if (!isWaitingForGtm) return

    const timeoutId = setTimeout(() => {
      if (!IS_PROD) {
        console.error(
          `GTM container ${GTM_ID || 'unknown'} did not load within ${GTM_LOAD_TIMEOUT_MS}ms; Google Analytics and Google Ads are not initialized`,
        )
      }
      sendAmplitudeData(EVENTS.GTM_LOAD_TIMEOUT, {
        gtm_id: GTM_ID || '',
        timeout_ms: GTM_LOAD_TIMEOUT_MS,
        page_path: window.location.pathname,
      })
    }, GTM_LOAD_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [isWaitingForGtm])

  useEffect(() => {
    if (document.getElementById('gtm') || IS_DEV) return

    if (!IS_PROD) {
      console.error(
        'GTM snippet is missing; the cookie banner is not loaded and Google Analytics and Google Ads start without consent defaults',
      )
    }
    sendAmplitudeData(EVENTS.GTM_NOT_CONFIGURED, {
      page_path: window.location.pathname,
    })
  }, [])

  useEffect(() => {
    if (amplitudeInited.current) return
    amplitudeInited.current = true
    initAmplitude(undefined, initCallback)
  }, [])

  useEffect(() => {
    const checkCookies = setInterval(() => {
      const id = Cookies.get(COOKIE_UUID_KEY)
      if (id) {
        initBI(id, initCallback)
        if (fbclid && FB_PIXEL_ID) {
          initFacebookPixel(String(FB_PIXEL_ID), { external_id: id })
          sendFacebookPixelTrack('PageView')
        }
        clearInterval(checkCookies)
      }
    }, 500)

    const timeoutId = setTimeout(() => {
      clearInterval(checkCookies)
    }, 30_000)

    return () => {
      clearInterval(checkCookies)
      clearTimeout(timeoutId)
    }
  }, [fbclid])

  useInlineScript(tiktokScript, 'TiktokJelly')

  useInlineScript(hotjarScript, 'hj')
}
